import type { CitedSource } from "@/shared/lib/citations";
import {
  parseCitedSources,
  parseLearningHistoryResolutions,
  type LearningHistoryResolution,
} from "@/features/chat/model/chatTypes";
import { useEffect, useRef, useState } from "react";
import {
  parseSseChunk,
  type ParsedTurnStreamEvent,
  type PipelineVerboseStep,
} from "@/features/chat/lib/turnStream";
import type { ClarificationRequest } from "@/features/chat/model/chatTypes";
import { normalizeClarificationRequest } from "@/features/chat/model/chatTypes";
import { apiFetch } from "@/shared/api/apiFetch";
import {
  applyAnswerPartEvent,
  EMPTY_ANSWER_PARTS,
  isAnswerPartEvent,
  type StreamAnswerParts,
} from "@/features/chat/lib/answerParts";
import { derivePhase, type TurnPhase } from "@/features/chat/lib/turnPhase";

export type TurnStreamStatus =
  | "idle"
  | "connecting"
  | "streaming"
  | "waiting_for_clarification"
  | "complete"
  | "error"
  | "terminated";

export type UseTurnStreamOptions = {
  afterStepIndex?: number;
  initialVerboseLog?: PipelineVerboseStep[];
  initialStreamedText?: string;
  initialFollowUpQuestions?: string[];
  initialClarificationRequest?: ClarificationRequest | null;
  /** Parts already streamed before a reconnect (see answerPartsFromStoredSteps). */
  initialAnswerParts?: StreamAnswerParts;
  /** When true, replayed pause events do not close the SSE connection (post-clarification resume). */
  resumeAfterClarification?: boolean;
  /** Bumps stream reconnect when the turn id is unchanged but a new tail is needed. */
  reconnectKey?: number;
  onClarificationRequired?: (request: ClarificationRequest, turnId: string) => void;
};

type UseTurnStreamResult = {
  verboseLog: PipelineVerboseStep[];
  currentVerboseStep: PipelineVerboseStep | null;
  streamedText: string;
  followUpQuestions: string[];
  clarificationRequest: ClarificationRequest | null;
  hasReceivedToken: boolean;
  status: TurnStreamStatus;
  error: string | null;
  summary: string | null;
  insights: string[] | null;
  why: string | null;
  whyPoints: string[] | null;
  action: string | null;
  actionPoints: string[] | null;
  sources: CitedSource[];
  businessImpact: string | null;
  learningHistoryResolutions: LearningHistoryResolution[];
  /** Table, SQL, chart and insights sent before the turn completes (see lib/answerParts). */
  answerParts: StreamAnswerParts;
  /** What the turn is doing now, in reader terms; null until the first event (lib/turnPhase). */
  phase: TurnPhase | null;
  /** Browser time the stream for this turn first opened; kept across a reconnect. */
  startedAt: number | null;
};

function maxStepIndex(steps: PipelineVerboseStep[]): number {
  if (steps.length === 0) {
    return 0;
  }
  return Math.max(...steps.map((step) => step.stepIndex));
}

function parseClarificationRequest(raw: Record<string, unknown> | undefined): ClarificationRequest | null {
  return normalizeClarificationRequest(raw);
}

export function useTurnStream(
  turnId: string | null,
  options?: UseTurnStreamOptions | null,
): UseTurnStreamResult {
  const [verboseLog, setVerboseLog] = useState<PipelineVerboseStep[]>([]);
  const [currentVerboseStep, setCurrentVerboseStep] = useState<PipelineVerboseStep | null>(null);
  const [streamedText, setStreamedText] = useState("");
  const [followUpQuestions, setFollowUpQuestions] = useState<string[]>([]);
  const [clarificationRequest, setClarificationRequest] = useState<ClarificationRequest | null>(
    null,
  );
  const [hasReceivedToken, setHasReceivedToken] = useState(false);
  const [status, setStatus] = useState<TurnStreamStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [insights, setInsights] = useState<string[] | null>(null);
  const [why, setWhy] = useState<string | null>(null);
  const [whyPoints, setWhyPoints] = useState<string[] | null>(null);
  const [action, setAction] = useState<string | null>(null);
  const [actionPoints, setActionPoints] = useState<string[] | null>(null);
  const [sources, setSources] = useState<CitedSource[]>([]);
  const [businessImpact, setBusinessImpact] = useState<string | null>(null);
  const [learningHistoryResolutions, setLearningHistoryResolutions] = useState<
    LearningHistoryResolution[]
  >([]);
  const [answerParts, setAnswerParts] = useState<StreamAnswerParts>(EMPTY_ANSWER_PARTS);
  const [phase, setPhase] = useState<TurnPhase | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const activeTurnIdRef = useRef<string | null>(null);
  const appliedStepIndexRef = useRef(0);
  // A reconnect for the same turn (resume, clarification) keeps the original start.
  const startedAtByTurnRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    if (!turnId) {
      activeTurnIdRef.current = null;
      appliedStepIndexRef.current = 0;
      setVerboseLog([]);
      setCurrentVerboseStep(null);
      setStreamedText("");
      setFollowUpQuestions([]);
      setClarificationRequest(null);
      setHasReceivedToken(false);
      setStatus("idle");
      setError(null);
      setSummary(null);
      setInsights(null);
      setWhy(null);
      setWhyPoints(null);
      setAction(null);
      setActionPoints(null);
      setSources([]);
      setBusinessImpact(null);
      setLearningHistoryResolutions([]);
      setAnswerParts(EMPTY_ANSWER_PARTS);
      setPhase(null);
      setStartedAt(null);
      return;
    }

    const abortController = new AbortController();
    activeTurnIdRef.current = turnId;

    const initialVerboseLog = options?.initialVerboseLog ?? [];
    const initialStreamedText = options?.initialStreamedText ?? "";
    const initialFollowUpQuestions = options?.initialFollowUpQuestions ?? [];
    const initialClarificationRequest = options?.initialClarificationRequest ?? null;
    const afterStepIndex =
      options?.afterStepIndex ?? (initialVerboseLog.length > 0 ? maxStepIndex(initialVerboseLog) : 0);

    appliedStepIndexRef.current = afterStepIndex;
    setVerboseLog(initialVerboseLog);
    setCurrentVerboseStep(
      initialVerboseLog.length > 0 ? initialVerboseLog[initialVerboseLog.length - 1] : null,
    );
    setStreamedText(initialStreamedText);
    setFollowUpQuestions(initialFollowUpQuestions);
    setClarificationRequest(
      options?.resumeAfterClarification ? null : initialClarificationRequest,
    );
    setHasReceivedToken(initialStreamedText.length > 0);
    setAnswerParts(options?.initialAnswerParts ?? EMPTY_ANSWER_PARTS);
    setPhase(
      initialVerboseLog.reduce<TurnPhase | null>((current, step) => derivePhase(current, step), null),
    );
    const turnStartedAt = startedAtByTurnRef.current.get(turnId) ?? Date.now();
    startedAtByTurnRef.current.set(turnId, turnStartedAt);
    setStartedAt(turnStartedAt);
    setStatus("connecting");
    setError(null);

    const consumeStream = async () => {
      try {
        const query =
          afterStepIndex > 0 ? `?after_step_index=${encodeURIComponent(String(afterStepIndex))}` : "";
        const response = await apiFetch(`/v1/chat/turns/${turnId}/stream${query}`, {
          method: "GET",
          headers: { Accept: "text/event-stream" },
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`Stream request failed with status ${response.status}.`);
        }

        if (!response.body) {
          throw new Error("Stream response did not include a body.");
        }

        if (activeTurnIdRef.current !== turnId) {
          return;
        }

        setStatus("streaming");

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let sawTerminalEvent = false;

        const handleEvent = (event: ParsedTurnStreamEvent): boolean => {
          if (event.type === "done") {
            sawTerminalEvent = true;
            if (event.summary) setSummary(event.summary);
            if (event.insights?.length) setInsights(event.insights);
            if (event.why) setWhy(event.why);
            if (event.why_points?.length) setWhyPoints(event.why_points);
            if (event.action) setAction(event.action);
            if (event.action_points?.length) setActionPoints(event.action_points);
            if (event.sources) setSources(parseCitedSources(event.sources));
            if (event.business_impact) setBusinessImpact(event.business_impact);
            if (event.learning_history_resolutions) {
              setLearningHistoryResolutions(
                parseLearningHistoryResolutions(event.learning_history_resolutions),
              );
            }
            setStatus("complete");
            return true;
          }

          if (event.type === "terminated") {
            sawTerminalEvent = true;
            setStatus("terminated");
            return true;
          }

          if (event.type === "error") {
            sawTerminalEvent = true;
            setStatus("error");
            setError(event.message ?? event.content ?? "The assistant run failed.");
            return true;
          }

          if (event.type === "token" && event.delta) {
            setHasReceivedToken(true);
            setStreamedText((current) => current + event.delta);
            setPhase((current) => derivePhase(current, { eventType: "token" }));
            return false;
          }

          if (isAnswerPartEvent(event)) {
            setAnswerParts((current) => applyAnswerPartEvent(current, event));
            const partPayload =
              event.answer_part && typeof event.answer_part === "object"
                ? (event.answer_part as Record<string, unknown>)
                : null;
            setPhase((current) =>
              derivePhase(current, {
                eventType: "answer_part",
                part: event.part,
                narrativePending: partPayload?.narrative_pending === true,
              }),
            );
            return false;
          }

          if (event.type === "follow_up_questions" && event.questions) {
            setFollowUpQuestions(event.questions);
            return false;
          }

          if (event.type === "clarification_required") {
            if (options?.resumeAfterClarification) {
              return false;
            }
            const request = parseClarificationRequest(event.request);
            if (request) {
              setClarificationRequest(request);
              options?.onClarificationRequired?.(request, turnId);
            }
            setStatus("waiting_for_clarification");
            return false;
          }

          if (event.type === "waiting_for_clarification") {
            if (options?.resumeAfterClarification) {
              return false;
            }
            const request = parseClarificationRequest(event.request);
            if (request) {
              setClarificationRequest(request);
              options?.onClarificationRequired?.(request, turnId);
            }
            sawTerminalEvent = true;
            setStatus("waiting_for_clarification");
            return true;
          }

          if (event.verboseStep) {
            const step = event.verboseStep as PipelineVerboseStep;
            if (step.stepIndex <= appliedStepIndexRef.current) {
              return false;
            }
            appliedStepIndexRef.current = step.stepIndex;
            setVerboseLog((current) => [...current, step]);
            setCurrentVerboseStep(step);
            setPhase((current) => derivePhase(current, step));
          }

          return false;
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const parsed = parseSseChunk(buffer);
          buffer = parsed.remainder;

          for (const event of parsed.events) {
            if (handleEvent(event)) {
              await reader.cancel();
              return;
            }
          }
        }

        if (activeTurnIdRef.current === turnId && sawTerminalEvent) {
          setStatus((current) =>
            current === "waiting_for_clarification" ? current : "complete",
          );
        }
      } catch (streamError) {
        if (abortController.signal.aborted) {
          return;
        }

        setStatus("error");
        setError(
          streamError instanceof Error
            ? streamError.message
            : "Unable to connect to the assistant stream.",
        );
      }
    };

    void consumeStream();

    return () => {
      abortController.abort();
      if (activeTurnIdRef.current === turnId) {
        activeTurnIdRef.current = null;
      }
    };
    // Individual option fields are listed so callers can pass a fresh options object each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- options identity is intentionally unstable
  }, [
    options?.afterStepIndex,
    options?.initialAnswerParts,
    options?.initialClarificationRequest,
    options?.initialFollowUpQuestions,
    options?.initialStreamedText,
    options?.initialVerboseLog,
    options?.onClarificationRequired,
    options?.reconnectKey,
    options?.resumeAfterClarification,
    turnId,
  ]);

  return {
    verboseLog,
    currentVerboseStep,
    streamedText,
    followUpQuestions,
    clarificationRequest,
    hasReceivedToken,
    status,
    error,
    summary,
    insights,
    why,
    whyPoints,
    action,
    actionPoints,
    sources,
    businessImpact,
    learningHistoryResolutions,
    answerParts,
    phase,
    startedAt,
  };
}
